/**
 * @file 字符串相关的 utils
 */
/**
 * 生成一个 4 位由字母和数字组成的随机验证码
 *
 * @category String
 * @returns generated pin code
 * @example
 * ```javascript
 * const code = getRandomPINCode() // => '2mz8'
 * ```
 */
export const getRandomPINCode = () => Math.random().toString(36).substr(2, 4);
/**
 * 生成一个 12 位左右, 可作为 uuid 的随机字符串
 *
 * @category String
 * @returns generated uuid
 * @example
 * ```javascript
 * const id = getRandomId() // => 'kk3dqh6t99589'
 * ```
 */
export const getRandomId = () => Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
/**
 * 生成一个指定长度的随机字符串
 *
 * @category String
 * @param length - 长度
 * @param charSet - 指定范围内的字符
 * @returns generated random string
 * @example
 * ```javascript
 * const random = getRandomString() // => '4VqdYiWzanAKvXW1'
 */
export const getRandomString = (length, charSet) => {
    const result = [];
    let _length = length || 16;
    const _charSet = charSet || 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    while (_length--) {
        result.push(_charSet[Math.floor(Math.random() * _charSet.length)]);
    }
    return result.join('');
};
/**
 * 安全解析 JSON String
 *
 * @category String
 * @param str - JSON String
 * @returns 解析后的 JSON Object，解析失败返回 null
 */
export function safeParseJSON(str) {
    if (!str)
        return null;
    try {
        return JSON.parse(str);
    }
    catch (e) {
        console.error(e);
        return null;
    }
}
/**
 * 将 string 转化为可被 regexp 正常识别的状态
 * 比如 '\w+' 会转化为 '\\w+'，这样 new Regexp() 时才能被正常转化
 *
 * @category String
 * @param str - string
 * @returns - 转化后的 string
 */
export function escapeStringRegExp(str) {
    return str.replace(/[|\\{}()[\]^$+*?.]/g, '\\$&');
}
/**
 * 截取字符串长度，同时增加一个后缀
 *
 * @category String
 * @param str - 字符串
 * @param maxLength - 最大长度
 * @param suffix - 后缀，默认 ...
 *
 * @returns 字符串
 */
export function ellpsisText(str, maxLength, suffix = '...') {
    if (str.length > maxLength) {
        return `${str.slice(0, maxLength)}${suffix}`;
    }
    return str;
}
/**
 * 移除字符串中的所有换行符
 *
 * @category String
 * @param text - 字符串
 *
 * @returns 不包含换行符的字符串
 */
export function removeLineBreaks(text) {
    return text.replace(/(\r\n|\n|\r)/gm, '');
}
//# sourceMappingURL=data:application/json;base64,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